
import { safeLocalStorage } from '../utils/safeStorage';
import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Task, Project, MediaType, Participant, Submission, Nudge, UserProfile } from '../types';
import ParticipantTaskCard from './ParticipantTaskCard';
import TaskSubmissionModal from './TaskSubmissionModal';
import SubmissionCard from './SubmissionCard';
import { CheckIcon, CheckSquareIcon, DatabaseIcon, WavingHandIcon } from './icons';
import ParticipantOnboarding from './ParticipantOnboarding';
import { mediaURLtoFile, needsUpload } from '../utils/mediaHelpers';
import { uploadMedia } from '../services/storageService';
import { isToday, isThisWeek, isThisMonth } from '../utils/dateHelpers';
import NudgeNotificationBanner from './NudgeNotificationBanner';

interface ParticipantViewProps {
  currentProject: Project | null;
  tasks: Task[];
  onCreateSubmission: (submissionData: { notes: string; mediaUrls: string[]; mediaType: MediaType }, taskPrompt: string, taskId?: string) => void;
  currentUser: Participant;
  onUpdateProfile: (updatedProfile: Participant) => void;
  submissions: Submission[];
  onSelectSubmission: (submission: Submission) => void;
  onDeleteSubmission: (submissionId: string) => void;
  nudges: Nudge[];
  onMarkNudgeAsRead: (nudgeId: string) => void;
}

interface SubmissionDraft {
  notes: string;
  mediaDataUrls: string[];
  mediaType?: MediaType;
}

const ParticipantView: React.FC<ParticipantViewProps> = ({ currentProject, tasks: allTasks, onCreateSubmission, currentUser, onUpdateProfile, submissions, onSelectSubmission, onDeleteSubmission, nudges, onMarkNudgeAsRead, userProfile, onSwitchToResearcherView }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [submittingTask, setSubmittingTask] = useState<Task | null>(null);
  const [inProgressSubmissions, setInProgressSubmissions] = useState<Record<string, SubmissionDraft>>({});
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState<'preparing' | 'uploading' | 'done' | null>(null);
  const [uploadTaskName, setUploadTaskName] = useState('');

  // Track tasks submitted while offline (prevents duplicate submissions)
  const [offlineSubmittedTaskIds, setOfflineSubmittedTaskIds] = useState<Set<string>>(new Set());

  // Filtering and Sorting state
  const [filterType, setFilterType] = useState('all'); // 'all', 'urgent', 'video', 'audio', 'photo', 'text', 'screen'
  const [sortType, setSortType] = useState('due-asc'); // 'due-asc', 'newest'

  // Filter unread nudges for current participant
  const unreadNudges = useMemo(() => {
    return nudges
      .filter(nudge => nudge.participantId === currentUser.id && !nudge.isRead)
      .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
  }, [nudges, currentUser.id]);

  /**
   * Check if a task is completed based on its recurrence type
   * - One-time tasks: completed if any submission exists
   * - Daily tasks: completed if there's a submission today
   * - Weekly tasks: completed if there's a submission this week
   * - Monthly tasks: completed if there's a submission this month
   */
  const isTaskCompleted = useMemo(() => {
    return (task: Task): boolean => {
      const taskSubmissions = submissions.filter(sub => sub.taskId === task.id);

      if (taskSubmissions.length === 0) return false;

      // One-time task or no recurrence specified
      if (!task.recurrence || task.recurrence === 'none') {
        return true;
      }

      // Daily task: check if there's a submission today
      if (task.recurrence === 'daily') {
        return taskSubmissions.some(sub => isToday(new Date(sub.timestamp)));
      }

      // Weekly task: check if there's a submission this week
      if (task.recurrence === 'weekly') {
        return taskSubmissions.some(sub => isThisWeek(new Date(sub.timestamp)));
      }

      // Monthly task: check if there's a submission this month
      if (task.recurrence === 'monthly') {
        return taskSubmissions.some(sub => isThisMonth(new Date(sub.timestamp)));
      }

      return false;
    };
  }, [submissions]);

  // Map task IDs to their most recent submissions
  const taskSubmissionMap = useMemo(() => {
    const map = new Map<string, Submission>();
    submissions.forEach(submission => {
      if (submission.taskId) {
        const existing = map.get(submission.taskId);
        // Keep the most recent submission
        if (!existing || new Date(submission.timestamp) > new Date(existing.timestamp)) {
          map.set(submission.taskId, submission);
        }
      }
    });
    return map;
  }, [submissions]);

  // Filter tasks assigned to this participant OR tasks they have already completed (for admins/testers)
  const assignedTasks = useMemo(() => {
    return allTasks.filter(task => {
      // 1. If user already completed it, it's relevant
      if (taskSubmissionMap.has(task.id)) return true;

      // 2. If no specific assignments, it's for everyone
      if (!task.assignedParticipantIds) {
        return true;
      }
      // 3. Check if current user is in the assigned list
      return task.assignedParticipantIds.includes(currentUser.id);
    });
  }, [allTasks, currentUser.id, taskSubmissionMap]);

  // Calculate completed tasks based on recurrence-aware logic + offline submissions
  const completedTaskIds = useMemo(() => {
    const completed = new Set<string>();
    assignedTasks.forEach(task => {
      if (isTaskCompleted(task) || offlineSubmittedTaskIds.has(task.id)) {
        completed.add(task.id);
      }
    });
    return completed;
  }, [assignedTasks, isTaskCompleted, offlineSubmittedTaskIds]);

  const activeTasks = assignedTasks.filter(t => !completedTaskIds.has(t.id));

  // Process sorting and filtering for active tasks
  const processedActiveTasks = useMemo(() => {
    let result = [...activeTasks];

    // 1. Filter
    if (filterType !== 'all') {
      const now = new Date().getTime();
      result = result.filter(task => {
        if (filterType === 'urgent') {
          const now = new Date().getTime();
          const timeLeft = new Date(task.dueDate).getTime() - now;
          const isSoon = timeLeft < 24 * 60 * 60 * 1000 && timeLeft > 0;
          return task.isUrgent || isSoon;
        }
        // Media type filters
        return task.mediaTypes.includes(filterType as MediaType);
      });
    }

    // 2. Sort
    result.sort((a, b) => {
      if (sortType === 'due-asc') {
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      } else if (sortType === 'newest') {
        return b.id.localeCompare(a.id);
      } else if (sortType === 'urgent-first') {
        const now = Date.now();
        const aUrgent = a.isUrgent || (new Date(a.dueDate).getTime() - now < 86400000 && new Date(a.dueDate).getTime() - now > 0);
        const bUrgent = b.isUrgent || (new Date(b.dueDate).getTime() - now < 86400000 && new Date(b.dueDate).getTime() - now > 0);
        if (aUrgent && !bUrgent) return -1;
        if (!aUrgent && bUrgent) return 1;
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      }
      return 0;
    });

    return result;
  }, [activeTasks, filterType, sortType]);

  // Gamification Logic - based on current period completions
  const totalTasksCount = assignedTasks.length;
  const completedCount = completedTaskIds.size;
  const progressPercentage = totalTasksCount > 0 ? Math.round((completedCount / totalTasksCount) * 100) : 0;

  useEffect(() => {
    // Simulate loading delay for better UX
    const timer = setTimeout(() => {
      const hasCompletedOnboarding = safeLocalStorage.getItem('hasCompletedParticipantOnboarding');
      if (!hasCompletedOnboarding) {
        // setShowOnboarding(true);
      }
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleStartTask = (task: Task) => {
    setSubmittingTask(task);
  };

  const handleOnboardingComplete = () => {
    safeLocalStorage.setItem('hasCompletedParticipantOnboarding', 'true');
    setShowOnboarding(false);
  };

  const handleCloseSubmissionModal = () => {
    setSubmittingTask(null);
  };

  const handleSaveDraft = (taskId: string, draft: SubmissionDraft) => {
    setInProgressSubmissions(prev => ({ ...prev, [taskId]: draft }));
    handleCloseSubmissionModal();
  };

  const handleTaskSubmit = async (taskToSubmit: Task, submissionData: { notes: string; mediaDataUrls: string[]; mediaType: MediaType }, isResubmit: boolean = false) => {
    setSubmittingTask(null); // Close modal

    // If resubmitting, delete the old submission first
    if (isResubmit) {
      const oldSubmission = taskSubmissionMap.get(taskToSubmit.id);
      if (oldSubmission) {
        await onDeleteSubmission(oldSubmission.id);
      }
    }

    // Use submissionData.mediaType if available, otherwise fall back to task's first media type
    const targetMediaType = submissionData.mediaType || taskToSubmit.mediaTypes[0];

    // ── Offline: refuse, don't fake it ──
    // This branch used to write the payload into a 'submissions_queue' store
    // that nothing ever drained, mark the task complete, and delete the draft —
    // so the submission was silently lost behind a success message, and the
    // media (held only as temporary blob: URLs) with it. Storage uploads still
    // can't proceed offline, so the honest behaviour is to stop and KEEP the
    // draft. The current participant flow (/p) queues offline uploads properly
    // via UploadManager; this legacy view has no such queue.
    if (!navigator.onLine) {
      alert('You are offline. Your draft has been kept — please submit again once you have a connection.');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadPhase('preparing');
      setUploadTaskName(taskToSubmit.prompt.length > 50 ? taskToSubmit.prompt.slice(0, 50) + '...' : taskToSubmit.prompt);

      const uploadedUrls: string[] = [];

      // Upload each media file to Firebase Storage
      for (let i = 0; i < submissionData.mediaDataUrls.length; i++) {
        const url = submissionData.mediaDataUrls[i];

        // Check if URL needs to be uploaded (is blob or data URL)
        if (needsUpload(url)) {
          // Convert URL to File
          const mediaTypeMap: Record<MediaType, 'photo' | 'video' | 'audio' | 'document' | 'screen'> = {
            [MediaType.Photo]: 'photo',
            [MediaType.Video]: 'video',
            [MediaType.Audio]: 'audio',
            [MediaType.Text]: 'document',
            [MediaType.Screen]: 'screen',
            [MediaType.Signification]: 'document',
          };

          const file = await mediaURLtoFile(url, i, mediaTypeMap[targetMediaType]);

          // Generate a unique submission ID for storage path
          const tempSubmissionId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          // Upload to Firebase Storage with progress tracking
          const metadata = await uploadMedia(
            file,
            mediaTypeMap[targetMediaType],
            tempSubmissionId,
            (progress) => {
              // Switch to uploading phase on first progress
              if (uploadPhase !== 'uploading') setUploadPhase('uploading');
              // Update upload progress
              const overallProgress = ((i + progress.progress / 100) / submissionData.mediaDataUrls.length) * 100;
              setUploadProgress(Math.round(overallProgress));
            }
          );

          // Add the permanent download URL
          uploadedUrls.push(metadata.downloadURL);
        } else {
          // Already a permanent URL (shouldn't happen, but handle it)
          uploadedUrls.push(url);
        }
      }

      // Create submission with uploaded URLs
      onCreateSubmission({
        notes: submissionData.notes,
        mediaUrls: uploadedUrls,
        mediaType: targetMediaType,
      }, taskToSubmit.prompt, taskToSubmit.id);

      // Clear any saved draft for the submitted task
      setInProgressSubmissions(prev => {
        const newDrafts = { ...prev };
        delete newDrafts[taskToSubmit.id];
        return newDrafts;
      });

      // Show done state briefly
      setUploadPhase('done');
      setUploadProgress(100);
      setTimeout(() => {
        setUploadPhase(null);
        setIsUploading(false);
        setUploadProgress(0);
        setUploadTaskName('');
      }, 2500);
    } catch (error) {
      console.error('Error uploading media:', error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      alert(`Upload failed: ${errorMsg}`);
      setUploadPhase(null);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadTaskName('');
    }
  };

  if (showOnboarding) {
    return <ParticipantOnboarding onComplete={handleOnboardingComplete} />;
  }

  const welcomeTitle = currentProject?.welcomeTitle
    ? currentProject.welcomeTitle
    : (currentUser.name ? `Welcome, ${currentUser.name.split(' ')[0]}!` : 'Welcome to the Study');

  return (
    <div className="mx-auto p-4 sm:p-6 lg:p-8 max-w-2xl relative">
      <header
        data-tutorial-id="welcome-header"
        className="bg-white rounded-2xl shadow-sm border border-app-border p-6 sm:p-8 mb-8 relative overflow-hidden"
      >
        <div className="relative z-10">
          <div className="w-10 h-10 mb-4 hand-container cursor-pointer">
            <WavingHandIcon className="w-full h-full text-action hand-icon" />
          </div>
          <h1 className="text-2xl font-bold text-text-ink tracking-tight">
            {welcomeTitle}
          </h1>
          <p className="mt-1.5 text-sm text-text-muted leading-relaxed whitespace-pre-wrap">
            {(currentProject && currentProject.welcomeMessage) || "Thank you for participating! Your contributions are incredibly valuable."}
          </p>

          {/* Progress Bar (Gamification) */}
          <div className="mt-6">
            <div className="flex justify-between items-end mb-1.5">
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Your Progress</span>
              <span className="text-[11px] font-bold text-text-ink uppercase tracking-wider">{completedCount} / {totalTasksCount} completed</span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E8E4DF' }}>
              <div
                className="h-full rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${progressPercentage}%`, backgroundColor: progressPercentage >= 100 ? '#6E7A5A' : progressPercentage > 0 ? '#7BBFAF' : 'transparent' }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Nudge Notifications */}
      {unreadNudges.length > 0 && (
        <div className="space-y-3 mb-6">
          {unreadNudges.map(nudge => (
            <NudgeNotificationBanner
              key={nudge.id}
              nudge={nudge}
              onDismiss={onMarkNudgeAsRead}
            />
          ))}
        </div>
      )}

      {/* Tab Navigation - REMOVED */}

      <main data-tutorial-id="task-list" className="space-y-6">
        {loading && (
          <div className="text-center py-10">
            <div className="w-12 h-12 border-4 border-t-action border-action/20 rounded-full animate-spin mx-auto"></div>
            <p className="text-text-muted mt-4 font-bold">Loading your journey...</p>
          </div>
        )}

        {/* Always show tasks view since tabs are removed */}
        <>
          {!loading && assignedTasks.length === 0 && (
            <div className="text-center py-16 bg-white rounded-xl border border-app-border shadow-sm">
              <div className="w-20 h-20 bg-status-ok-bg rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckIcon className="w-10 h-10 text-status-ok" />
              </div>
              <h2 className="text-2xl font-bold text-text-ink">No Tasks Available</h2>
              <p className="mt-2 text-text-muted max-w-xs mx-auto">Check back later for new activities.</p>
            </div>
          )}
          {!loading && activeTasks.length > 0 && (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Active Tasks</h2>
                <div className="flex items-center gap-2">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="bg-white/80 backdrop-blur-md border border-app-border/80 text-action text-xs font-semibold rounded-full pl-3.5 pr-9 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-action/20 hover:border-action/30 hover:bg-white transition-all appearance-none cursor-pointer"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='2.5' stroke='%23D4715A80'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '0.85em 0.85em' }}
                  >
                    <option value="all">All Types</option>
                    <option value="urgent">Urgent</option>
                    <option value={MediaType.Video}>Video</option>
                    <option value={MediaType.Audio}>Audio</option>
                    <option value={MediaType.Photo}>Photo</option>
                    <option value={MediaType.Text}>Text</option>
                    <option value={MediaType.Screen}>Screen</option>
                  </select>
                  <select
                    value={sortType}
                    onChange={(e) => setSortType(e.target.value)}
                    className="bg-white/80 backdrop-blur-md border border-app-border/80 text-action text-xs font-semibold rounded-full pl-3.5 pr-9 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-action/20 hover:border-action/30 hover:bg-white transition-all appearance-none cursor-pointer"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='2.5' stroke='%23D4715A80'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '0.85em 0.85em' }}
                  >
                    <option value="due-asc">Due Soonest</option>
                    <option value="newest">Newest First</option>
                    <option value="urgent-first">Urgent First</option>
                  </select>
                </div>
              </div>

              {processedActiveTasks.length === 0 ? (
                <div className="text-center py-8 bg-white/50 rounded-2xl border border-dashed border-app-border">
                  <p className="text-text-muted text-sm font-medium">No active tasks match these filters.</p>
                  <button onClick={() => setFilterType('all')} className="mt-2 text-action text-xs font-bold hover:underline">Clear Filters</button>
                </div>
              ) :
                processedActiveTasks.map((task, index) => (
                  <ParticipantTaskCard
                    key={task.id}
                    task={task}
                    onStartTask={handleStartTask}
                    hasDraft={!!inProgressSubmissions[task.id]}
                    hasMedia={!!inProgressSubmissions[task.id]?.mediaDataUrls?.length}
                    isFirstTask={index === 0}
                    isCompleted={false}
                    onResubmit={handleStartTask}
                    cardIndex={index}
                  />
                ))}
            </>
          )}
          {!loading && assignedTasks.filter(t => completedTaskIds.has(t.id)).length > 0 && (
            <>
              <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4 mt-8">Completed Tasks</h2>
              {assignedTasks.filter(t => completedTaskIds.has(t.id)).map((task, index) => (
                <ParticipantTaskCard
                  key={task.id}
                  task={task}
                  onStartTask={handleStartTask}
                  hasDraft={false}
                  isFirstTask={false}
                  isCompleted={true}
                  isPendingUpload={offlineSubmittedTaskIds.has(task.id)}
                  existingSubmission={taskSubmissionMap.get(task.id)}
                />
              ))}
            </>
          )}
          {!loading && activeTasks.length === 0 && assignedTasks.filter(t => completedTaskIds.has(t.id)).length > 0 && (
            <div className="text-center py-8 bg-white rounded-xl border border-app-border shadow-sm mb-6">
              <div className="w-16 h-16 bg-status-ok-bg rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckIcon className="w-8 h-8 text-status-ok" />
              </div>
              <h2 className="text-xl font-bold text-text-ink">All Tasks Completed!</h2>
              <p className="mt-2 text-sm text-text-muted max-w-xs mx-auto">You can resubmit any task below if needed.</p>
            </div>
          )}
        </>
      </main>

      {submittingTask && (
        <TaskSubmissionModal
          task={submittingTask}
          onClose={handleCloseSubmissionModal}
          onSubmit={(data) => {
            const isResubmit = completedTaskIds.has(submittingTask.id);
            handleTaskSubmit(submittingTask, data, isResubmit);
          }}
          onSaveDraft={handleSaveDraft}
          initialDraft={inProgressSubmissions[submittingTask.id]}
        />
      )}

      {/* Fixed bottom upload progress bar — rendered via portal to bypass ancestor CSS that breaks fixed positioning */}
      {uploadPhase && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999 }}
          className={`transition-opacity duration-500 ${uploadPhase === 'done' ? 'animate-fade-out' : ''}`}
        >
          {/* Sleek thin progress track */}
          <div className="h-[3px] w-full" style={{ background: 'rgba(255,255,255,0.3)' }}>
            <div
              className={`h-full transition-all duration-700 ease-out ${uploadPhase === 'preparing' ? 'animate-pulse' : ''}`}
              style={{ width: uploadPhase === 'preparing' ? '15%' : `${uploadProgress}%`, background: 'white' }}
            />
          </div>
          {/* Upload info bar on seafoam */}
          <div className="px-4 py-2.5 flex items-center gap-3 bg-upload-bg backdrop-blur-md border-t border-white/10 shadow-[0_-2px_12px_rgba(0,0,0,0.1)]">
            {uploadPhase === 'done' ? (
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <CheckIcon className="w-3.5 h-3.5 text-white" />
              </div>
            ) : (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black truncate text-white">
                {uploadPhase === 'preparing' && 'Preparing your submission...'}
                {uploadPhase === 'uploading' && `Uploading... ${uploadProgress}%`}
                {uploadPhase === 'done' && 'Submitted successfully!'}
              </p>
              {uploadTaskName && uploadPhase !== 'done' && (
                <p className="text-white/75 text-xs font-bold truncate">{uploadTaskName}</p>
              )}
            </div>
            {uploadPhase === 'uploading' && (
              <span className="text-upload-bg bg-white text-[10px] font-black px-2 py-0.5 rounded-full tabular-nums flex-shrink-0">
                {uploadProgress}%
              </span>
            )}
          </div>
          <div className="bg-upload-bg" style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
        </div>,
        document.body
      )}

    </div>
  );
};

export default ParticipantView;

/* Inject keyframes for waving hand animation */
if (typeof document !== 'undefined' && !document.getElementById('header-animations')) {
  const style = document.createElement('style');
  style.id = 'header-animations';
  style.textContent = `
    @keyframes hand-shake {
      0% { transform: rotate(0deg); }
      25% { transform: rotate(-10deg); }
      50% { transform: rotate(0deg); }
      75% { transform: rotate(10deg); }
      100% { transform: rotate(0deg); }
    }
    .hand-icon {
      transform: rotate(0deg);
      transform-origin: bottom center;
      transition: transform 0.3s ease;
    }
    .hand-container:hover .hand-icon {
      animation: hand-shake 0.6s ease-in-out 4;
    }
  `;
  document.head.appendChild(style);
}

